import React, { useEffect, useMemo, useState } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import { Button, Select, Text } from 'v3/components';
import api from 'services/api';
import { VEHICLE_PLATE_QUESTION_SLUG } from 'v3/pages/Checklist/Questions/shared/utils';

function normalizeOptions(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (data && typeof data === 'object') return [data];
  return [];
}

async function fetchVehiclePlate(search) {
  try {
    const response = await api.get('vehicle/license_plate', { params: { search } });
    return normalizeOptions(response?.data);
  } catch (error) {
    return [];
  }
}

async function fetchImplementPlate(search) {
  try {
    const response = await api.get('plate/wagons', { params: { search } });
    return normalizeOptions(response?.data);
  } catch (error) {
    return [];
  }
}

export default function PlateQuestionsModal({
  open,
  items = [],
  loading = false,
  error = '',
  onClose,
  onConfirm,
}) {
  const [values, setValues] = useState({});

  useEffect(() => {
    if (open) setValues({});
  }, [open, items]);

  const missingSelection = useMemo(() => items.some((item) => !values[item.key]), [items, values]);

  if (!open) return null;

  function confirm() {
    if (missingSelection || loading) return;
    onConfirm?.(items.map((item) => ({ item, option: values[item.key] })));
  }

  return (
    <div
      role="presentation"
      onClick={() => !loading && onClose?.()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2600,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="plate-link-modal-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 640,
          maxHeight: '90vh',
          overflow: 'hidden',
          borderRadius: 16,
          background: '#FFFFFF',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.24)',
        }}
      >
        <div
          style={{
            padding: '20px 22px 16px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: '#EAF2FA',
              color: '#174A73',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <DirectionsCarOutlinedIcon style={{ fontSize: 20 }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              id="plate-link-modal-title"
              style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}
            >
              Preencha as placas para gerar o link
            </div>
            <Text color="gray" style={{ display: 'block', marginTop: 4, fontSize: 13 }}>
              As placas serão fixadas no checklist e não poderão ser alteradas pelo respondente
              externo.
            </Text>
          </div>

          <button
            type="button"
            onClick={() => !loading && onClose?.()}
            disabled={loading}
            aria-label="Fechar"
            style={{
              border: 0,
              background: 'transparent',
              padding: 4,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            <CloseOutlinedIcon style={{ fontSize: 20, color: '#4B5563' }} />
          </button>
        </div>

        <div style={{ padding: 22, overflowY: 'auto', maxHeight: '60vh' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 18 }}>
            {items.map((item) => {
              const isVehicle = item.slug === VEHICLE_PLATE_QUESTION_SLUG;
              return (
                <Select.Async
                  key={item.key}
                  id={`external-link-plate-${item.key}`}
                  label={
                    item.label ||
                    (isVehicle ? 'Placa do veículo de tração' : 'Placa do implemento / Carroceria')
                  }
                  placeholder="Busque pela placa..."
                  onSearch={isVehicle ? fetchVehiclePlate : fetchImplementPlate}
                  value={values[item.key] || null}
                  onChange={(value) =>
                    setValues((previous) => ({ ...previous, [item.key]: value }))
                  }
                  removeInputMin
                  useMenuPortal
                  getOptionLabel={(option) => {
                    let label = String(option?.plate || '').toUpperCase();
                    if (option?.vehicle?.plate) {
                      label += ` - Tração ${String(option.vehicle.plate).toUpperCase()}`;
                    }
                    return label;
                  }}
                  getOptionValue={(option) => option?.id}
                  isDisabled={loading}
                  error={!values[item.key] && error ? 'Selecione uma placa.' : undefined}
                />
              );
            })}
          </div>

          {!!error && (
            <div style={{ color: '#B91C1C', fontSize: 13, fontWeight: 600, marginTop: 16 }}>
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '16px 22px 20px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            style={{ minWidth: 110 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirm}
            disabled={missingSelection || loading}
            loading={loading}
            style={{ minWidth: 150 }}
          >
            Confirmar e gerar
          </Button>
        </div>
      </div>
    </div>
  );
}
